import { AppLoader, Button, Flex, Loader, Steps, Text } from '@components';
import {
  useGetInvestmentIntentQuery,
  useCreateInvestmentIntentMutation,
  useUpdateInvestmentIntentMutation,
  useGetMinimumInvestmentAmountQuery,
} from '@features/api/investmentSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { investmentAmountFormSchema } from '@validationSchemas/investment';
import { Config } from '@config/config';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer, StyledInput } from './Amount.styles';
import { AmountFormData } from './Amount.types';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { InvestmentContainer } from '../Investment.styles';
import { useUserQuery } from '@features/api/userSlice';
import { handleNextButtonNavigation } from './Amount.utils';
import { useUserTransactionsQuery } from '@features/api/userPortfolioSlice';
import { TOTAL_STEPS } from '../Investment.consts';
import { useCompanyQuery } from '@features/api/companySlice';
import { useUltimateBeneficialOwnersQuery } from '@features/api/ultimateBeneficialOwnerSlice';
import { MinimumInvestmentAmount } from '@features/api/response/InvestmentIntent';
import { useCompanyAuthorizedSignatoriesQuery } from '@features/api/companySignatoriesSlice';
import { UserType } from '@features/api/response/User';

const currencies = [
  {
    value: 'USD',
    label: 'USD',
  },
  {
    value: 'EUR',
    label: 'EUR (hedged)',
  },
  {
    value: 'CHF',
    label: 'CHF (hedged)',
  },
];

const Amount: React.FC<
  WithUserProps & { minInvestmentAmount: MinimumInvestmentAmount }
> = ({ minInvestmentAmount }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { data: user } = useUserQuery();
  const { data: transactions } = useUserTransactionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCompanyHolder =
    user?.userType === UserType.CompanyHolder ||
    user?.userType === UserType.NomineeCompanyHolder;
  const { data: company } = useCompanyQuery(undefined, {
    skip: !isCompanyHolder,
  });
  const { data: ultimateBeneficialOwners } = useUltimateBeneficialOwnersQuery(
    undefined,
    { skip: !isCompanyHolder }
  );
  const { data: signatories } = useCompanyAuthorizedSignatoriesQuery(
    undefined,
    {
      skip: !isCompanyHolder,
    }
  );

  const {
    data: investmentIntent,
    isFetching,
    isLoading,
  } = useGetInvestmentIntentQuery(
    {
      productId: productId ?? '',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [createInvestmentIntentMutation] = useCreateInvestmentIntentMutation();
  const [updateInvestmentIntentMutation] = useUpdateInvestmentIntentMutation();

  const [selectedCurrency, setSelectedCurrency] = useState<string>(
    investmentIntent?.currency ?? 'USD'
  );

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<AmountFormData>({
    resolver: yupResolver(investmentAmountFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    context: {
      minimumAmount: minInvestmentAmount?.minAmount ?? 125000,
    },
    defaultValues: {
      amount: investmentIntent?.amount
        ? intl.formatNumber(Number(investmentIntent?.amount), {
            style: 'currency',
            currency: selectedCurrency ?? Config.currency.code,
            maximumFractionDigits: 0,
            notation: 'standard',
          })
        : undefined,
    },
  });

  const formatAmount = useCallback(
    (amount: number) => {
      return intl.formatNumber(amount, {
        style: 'currency',
        currency: selectedCurrency ?? Config.currency.code,
        maximumFractionDigits: 0,
        notation: 'standard',
      });
    },
    [intl, selectedCurrency]
  );

  useEffect(() => {
    if (investmentIntent?.amount) {
      setValue('amount', formatAmount(investmentIntent.amount));
      trigger('amount');
    }
  }, [investmentIntent?.amount, trigger, setValue, formatAmount]);

  useEffect(() => {
    if (investmentIntent?.currency) {
      setSelectedCurrency(investmentIntent.currency);
    }
  }, [investmentIntent?.currency, setSelectedCurrency]);

  const currentAmount = watch('amount');
  useEffect(() => {
    if (currentAmount) {
      const parsedNumber = Number(currentAmount.replace(/[^0-9.-]+/g, ''));
      setValue('amount', formatAmount(parsedNumber));
    }
  }, [currentAmount, formatAmount, setValue, trigger]);

  const onSubmit = async (data: AmountFormData) => {
    if (!data.amount) return;
    try {
      if (!investmentIntent?.id && productId) {
        await createInvestmentIntentMutation({
          amount: Number(data.amount.replace(/[^0-9.-]+/g, '')),
          currency: selectedCurrency,
          productId: productId,
        }).unwrap();
      } else if (investmentIntent?.id) {
        await updateInvestmentIntentMutation({
          amount: Number(data.amount.replace(/[^0-9.-]+/g, '')),
          currency: selectedCurrency,
          transactionId: investmentIntent?.id,
        }).unwrap();
      }

      const nextFn = handleNextButtonNavigation(
        navigate,
        user,
        company,
        ultimateBeneficialOwners,
        signatories,
        productId,
        transactions
      );
      if (nextFn) {
        nextFn();
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!minInvestmentAmount && !isLoading && !isFetching) {
    return (
      <Flex height='100%' alignItems='center' justifyContent='center'>
        <Text variant='h4' fontWeight='fontWeightMedium'>
          <FormattedMessage id='investment.amount.errorOccurredInvestmentInfo' />
        </Text>
      </Flex>
    );
  }

  if (isLoading || isFetching)
    return <Loader isLoading={isLoading || isFetching} />;

  return (
    <InvestmentContainer>
      <ContentContainer>
        <Steps currentStep={1} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.amount.investmentAmount' />
        </Text>
        <Text
          variant='h6'
          mt={12}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          <FormattedMessage id='investment.amount.currencyMessage' />
        </Text>
        <Flex mt={9} mb={14} gap={6}>
          {currencies.map((currency) => (
            <Button
              key={currency.value}
              onClick={() => setSelectedCurrency(currency.value)}
              variant={
                selectedCurrency === currency.value ? 'primary' : 'secondary'
              }
            >
              {currency.label}
            </Button>
          ))}
        </Flex>
        <Text
          variant='h6'
          mt={5}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          <FormattedMessage id='investment.amount.message' />
        </Text>
        <StyledInput
          label={intl.formatMessage(
            {
              id: 'investment.amount.amountInUSD',
            },
            {
              currencyCode: selectedCurrency ?? Config.currency.code,
            }
          )}
          fullWidth
          inputProps={{
            'data-testid': 'amount-input',
          }}
          {...registerInput(register, 'amount')}
          {...getErrorProps(intl, errors.amount)}
          onBlur={() =>
            setValue(
              'amount',
              formatAmount(
                Math.round(
                  Number(currentAmount.replace(/[^0-9.-]+/g, '')) / 1000
                ) * 1000
              )
            )
          }
        />
        <Text
          variant='body'
          fontWeight='fontWeightSemiBold'
          color={theme.palette.primary.main}
          mt={5}
        >
          <FormattedMessage
            id='investment.amount.minimumCommitment'
            values={{
              minCommitment: intl.formatNumber(
                minInvestmentAmount?.minAmount ?? 125000,
                {
                  style: 'currency',
                  currency: selectedCurrency ?? Config.currency.code,
                  minimumFractionDigits: 0,
                }
              ),
              nearestStep: intl.formatNumber(1000, {
                style: 'currency',
                currency: selectedCurrency ?? Config.currency.code,
                minimumFractionDigits: 0,
              }),
            }}
          />
        </Text>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () => navigate(-1),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

const AmountWithMinInvestment: React.FC<WithUserProps> = ({
  user,
  refetchUser,
}) => {
  const {
    data: minInvestmentAmount,
    isFetching: isFetchingMinAmount,
    isLoading: isLoadingMinAmount,
  } = useGetMinimumInvestmentAmountQuery();

  if (isFetchingMinAmount || isLoadingMinAmount) return <AppLoader />;
  if (!minInvestmentAmount) {
    return (
      <Flex height='100%' alignItems='center' justifyContent='center'>
        <Text variant='h4' fontWeight='fontWeightMedium'>
          <FormattedMessage id='investment.amount.errorOccurredInvestmentInfo' />
        </Text>
      </Flex>
    );
  }

  return (
    <Amount
      user={user}
      refetchUser={refetchUser}
      minInvestmentAmount={minInvestmentAmount}
    />
  );
};

export default WithUser(AmountWithMinInvestment);
